import type { FirmDomain } from "@/shared/utils/firm";

export interface WorkWeek {
	/** 0 = Sun, 1 = Mon, ..., 6 = Sat */
	startDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	length: 5 | 6 | 7;
}

/**
 * Per-firm defaults for the Entries view. Firms that don't surface certain
 * columns can start them hidden without removing them — users can still turn
 * them back on from the table's Columns menu.
 */
export interface EntriesDisplayConfig {
	/**
	 * Column ids hidden by default in the entries table. These remain listed in
	 * the Columns menu, so users can re-enable them; the setting only changes the
	 * out-of-the-box default.
	 */
	defaultHiddenColumns: readonly string[];
}

export interface HardcodedParams {
	displayName: string;
	/**
	 * Calendar grid only — date pickers ignore this and use the react-day-picker
	 * default (Sunday).
	 */
	weekStartsOn: 0 | 1;
	/** Defaults to Sun→Sat (`DEFAULT_WORK_WEEK`) when unset. */
	workWeek?: WorkWeek;
	/**
	 * Entries table column defaults. Defaults to `DEFAULT_ENTRIES_DISPLAY`
	 * (nothing hidden) when unset.
	 */
	entriesDisplay?: EntriesDisplayConfig;
}

/**
 * Per-firm feature flags.
 * `useHardcodedParams()` always returns a fully-typed, non-undefined value.
 */
export const HARDCODED_PARAMS: Record<FirmDomain, HardcodedParams> = {
	hourglass: { displayName: "Hourglass", weekStartsOn: 0 },
	pregodonnell: { displayName: "Preg O'Donnell & Gillett", weekStartsOn: 0 },
	tysonmendes: {
		displayName: "Tyson & Mendes, LLP",
		weekStartsOn: 1,
		workWeek: { startDay: 1, length: 7 },
	},
	bpmlaw: { displayName: "BPM Law", weekStartsOn: 0 },
	taylorday: {
		displayName: "Taylor Day Law",
		weekStartsOn: 0,
		// Taylor Day doesn't track billable status — start the Billing column
		// hidden (still available in the Columns menu).
		entriesDisplay: { defaultHiddenColumns: ["billableCategory"] },
	},
	namanhowell: { displayName: "Naman Howell", weekStartsOn: 0 },
	gouldratner: { displayName: "Gould & Ratner", weekStartsOn: 0 },
	test: {
		displayName: "Test",
		weekStartsOn: 0,
		// Mirror Taylor Day: start the Billing column hidden by default.
		entriesDisplay: { defaultHiddenColumns: ["billableCategory"] },
	},
	unknown: { displayName: "Hourglass", weekStartsOn: 0 },
};

export const DEFAULT_WORK_WEEK: WorkWeek = { startDay: 0, length: 7 };

/** Default when a firm doesn't override `entriesDisplay`: nothing hidden. */
export const DEFAULT_ENTRIES_DISPLAY: EntriesDisplayConfig = {
	defaultHiddenColumns: [],
};
